import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../utils/toast';
import { ChevronLeftIcon, ChevronRightIcon, EyeIcon } from '@heroicons/react/24/outline';

import calendarIcon from '../../assets/marketplaceCards/calendarIcon.svg';
import gloveIcon from '../../assets/marketplaceCards/gloveIcon.svg';
import mapPinIcon from '../../assets/marketplaceCards/mapPinIcon.svg';
import { formatPurse, humanize } from '../../pages/marketPlaceCards/marketCardHelpers';
import { getCardViewStats } from '../../services/api';
import { MarketplaceCardViewDto } from '../../types/cardViewStatsTypes';
import { getEventInitials } from '../../utils/common/formatUtils';
import { EmptyState } from './emptyState';
import { Skeleton } from './skeleton';

interface MarketplaceCardsProps {
  username: string;
  /** Profile photo of the logged in user - the tracking API doesn't return images */
  profileImage?: string;
  fullName?: string;
}

interface CardRowProps {
  icon: string;
  text: string;
}

// Hidden completely when the card has no value, so cards never show empty rows
const CardRow: React.FC<CardRowProps> = ({ icon, text }) =>
  text ? (
    <div className="flex min-w-0 items-center gap-2 text-sm leading-5 text-gray-700">
      <img src={icon} alt="" aria-hidden="true" className="h-4 w-4 shrink-0" />
      <span className="truncate" title={text}>
        {text}
      </span>
    </div>
  ) : null;

const formatCreatedDate = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const MarketplaceCardItem: React.FC<{
  card: MarketplaceCardViewDto;
  profileImage?: string;
  fullName?: string;
  onOpen: () => void;
}> = ({ card, profileImage, fullName, onOpen }) => {
  const { t } = useTranslation('dashboard');
  const details = card.cardResponseDto;
  const purse = formatPurse(details?.purse, details?.currency ?? undefined);
  const sportAndWeight = [humanize(details?.sport), humanize(details?.weightClass)]
    .filter(Boolean)
    .join(' • ');
  const viewsLabel = t('views_count', {
    count: card.views,
    defaultValue: card.views === 1 ? '{{count}} view' : '{{count}} views',
  });

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-60 shrink-0 snap-start flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-3 text-left transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
    >
      <div className="relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-xl bg-gray-100 text-2xl font-extrabold text-gray-500">
        {profileImage ? (
          <img
            src={profileImage}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            className="h-full w-full object-cover object-top"
          />
        ) : (
          getEventInitials(fullName)
        )}
        <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 text-xs font-semibold text-gray-900 shadow-sm">
          <EyeIcon className="h-3.5 w-3.5" />
          {viewsLabel}
        </span>
      </div>

      <div className="flex flex-col">
        <span className="text-xs leading-4 text-gray-500">
          {t('purse', { defaultValue: 'Purse' })}
        </span>
        <span className="truncate text-xl font-extrabold leading-7 text-gray-900">
          {purse || '—'}
        </span>
      </div>

      <div className="flex flex-col gap-2 border-t border-slate-100 pt-3">
        <CardRow icon={gloveIcon} text={sportAndWeight} />
        <CardRow icon={mapPinIcon} text={details?.location ?? ''} />
        <CardRow icon={calendarIcon} text={formatCreatedDate(details?.createdAt)} />
      </div>
    </button>
  );
};

export const MarketplaceCards: React.FC<MarketplaceCardsProps> = ({
  username,
  profileImage,
  fullName,
}) => {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [cards, setCards] = useState<MarketplaceCardViewDto[]>([]);
  const [totalViews, setTotalViews] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let isCancelled = false;

    // range "all" -> every view ever (backend only filters for "7days" / "month"), most viewed first
    getCardViewStats(username, { range: 'all', viewsSort: 'desc' })
      .then((data) => {
        if (isCancelled) return;
        setCards(data?.markrtplaceCardViews ?? []);
        setTotalViews(data?.totalViews ?? 0);
      })
      .catch(() => {
        if (!isCancelled) toast.error(t('failed_to_load_marketplace_cards'));
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [username, t]);

  const scrollTrack = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (track) track.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });
  };

  const isEmpty = !isLoading && cards.length === 0;

  return (
    <div className="flex flex-col gap-4 rounded-3xl bg-white p-4 shadow-sm ring-1 ring-black/5 font-['Plus_Jakarta_Sans',sans-serif]">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium leading-5 text-gray-900">{t('marketplace_cards')}</p>
        {!isLoading && !isEmpty && (
          <span className="text-xs font-medium leading-4 text-gray-500">
            {t('total_views', { defaultValue: 'Total views' })}:{' '}
            <span className="font-bold text-gray-900">{totalViews}</span>
          </span>
        )}
      </div>

      {isLoading ? (
        <div role="status" aria-busy="true" className="flex gap-4 overflow-hidden">
          {Array.from({ length: 2 }).map((_, index) => (
            <div
              key={index}
              className="flex w-60 shrink-0 flex-col gap-3 rounded-2xl border border-slate-200 p-3"
            >
              <Skeleton className="aspect-[4/3] w-full rounded-xl" />
              <Skeleton className="h-7 w-28" />
              <div className="flex flex-col gap-2 border-t border-slate-100 pt-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-28" />
              </div>
            </div>
          ))}
        </div>
      ) : isEmpty ? (
        <EmptyState text={t('no_data_to_display')} />
      ) : (
        <>
          <div
            ref={trackRef}
            className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {cards.map((card) => (
              <MarketplaceCardItem
                key={String(card.cardId)}
                card={card}
                profileImage={profileImage}
                fullName={fullName}
                onOpen={() => navigate('/marketPlaceCards')}
              />
            ))}
          </div>

          <div className="flex items-center justify-center gap-4">
            <button
              type="button"
              aria-label={t('previous_slide')}
              onClick={() => scrollTrack(-1)}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label={t('next_slide')}
              onClick={() => scrollTrack(1)}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};
